/**
 * App-level confirmation dialog (audit MAINT-6).
 *
 * Replaces scattered blocking `window.confirm()` calls with one styled,
 * accessible modal driven by an imperative promise API:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete contact?', destructive: true })) {
 *     deleteMutation.mutate();
 *   }
 *
 * A single dialog instance is rendered here; `useConfirm` returns a stable
 * function that opens it and resolves to the user's choice. Because the
 * controller settles exactly once, a second confirm click (or Esc during the
 * action) cannot fire the destructive callback twice — closing the
 * double-submit gap the native dialog avoided by blocking the thread.
 */
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import {
  createConfirmController,
  type ConfirmController,
  type ConfirmOptions,
} from '../lib/confirm-controller';

import { Button, Modal } from './ds';

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }): JSX.Element {
  const controllerRef = useRef<ConfirmController | null>(null);
  controllerRef.current ??= createConfirmController();
  const controller = controllerRef.current;

  const state = useSyncExternalStore(controller.subscribe, controller.getState);
  const confirm = useCallback<ConfirmFn>((options) => controller.request(options), [controller]);

  const opts = state.options;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={state.open}
        onClose={() => controller.settle(false)}
        title={opts?.title ?? ''}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => controller.settle(false)}>
              {opts?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={opts?.destructive ? 'danger' : 'primary'}
              onClick={() => controller.settle(true)}
            >
              {opts?.confirmLabel ?? 'Confirm'}
            </Button>
          </div>
        }
      >
        <div className="text-sm text-ink-muted">{opts?.message ?? 'Are you sure?'}</div>
      </Modal>
    </ConfirmContext.Provider>
  );
}

/** Returns an imperative `confirm(options) => Promise<boolean>`. */
export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm must be used within <ConfirmProvider>');
  }
  return confirm;
}
